import {
  Button,
  Card,
  CaretRightIcon,
  Heading,
  Pane,
  Spinner,
  Text,
  majorScale
} from 'evergreen-ui'
import { FC, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { CONSTANTS } from '../../../constants'
import { useGetRatingsQuery } from '../../../services/ratings/ratings.service'
import { User } from '../../../types'
import {
  WidgetDetails,
  generateWidgetDetails,
} from '../../../utils/generate-widget-details'
import WidgetDetailCard from '../../WidgetDetailCard/WidgetDetailCard'

interface RatingsWidgetProps {
  user?: User
  limit?: number
  title?: string
  showHeading?: boolean
  search?: string
}

const RatingsWidget: FC<RatingsWidgetProps> = (props: RatingsWidgetProps) => {
  const [skipCallingApiOnLoad, setSkipCallingApi] = useState(true)
  const [widgetData, setWidgetData] = useState<WidgetDetails[]>([])
  const [filteredData, setFilteredData] = useState<WidgetDetails[]>([])
  const ratingsLimit = props.limit
  const ratingValue = CONSTANTS.RATINGS.VALUES.UPVOTE

  const { isLoading, data } = useGetRatingsQuery(
    {
      user_id: props.user?.id,
      limit: `${ratingsLimit}`,
      value: `${ratingValue}`,
    },
    {
      skip: skipCallingApiOnLoad,
      refetchOnMountOrArgChange: true,
    },
  )

  // call the API once we have the User object
  useEffect(() => {
    if (props.user) {
      setSkipCallingApi(false)
    }
  }, [props.user])

  // set data on load
  useEffect(() => {
    if (data) {
      const widgetData = data.map((d) => generateWidgetDetails(d, 'RATING'))
      setWidgetData(widgetData)
      setFilteredData(widgetData)
    }
  }, [data])

  // search
  useEffect(() => {
    if (props.search) {
      setFilteredData(
        widgetData.filter(
          (f) =>
            f.content
              .toLowerCase()
              .includes(props.search?.toLowerCase() ?? '') ||
            f.title.toLowerCase().includes(props.search?.toLowerCase() ?? ''),
        ),
      )
    } else {
      setFilteredData(widgetData)
    }
  }, [props.search])

  return (
    <Card
      border
      padding={majorScale(2)}
      display="flex"
      flexDirection="column"
      background="white"
      elevation={1}
    >
      {props.showHeading ? (
        <Pane
          display="flex"
          justifyContent="space-between"
          borderBottom="1px dotted #7A8A99;"
          paddingBottom={majorScale(1)}
          alignItems="center"
          marginBottom={majorScale(2)}
        >
          <Heading size={600}>{props.title}</Heading>
          <Button
            is={NavLink}
            to={CONSTANTS.ROUTES.ALL_LIKES}
            appearance="minimal"
            iconAfter={CaretRightIcon}
          >
            View all likes
          </Button>
        </Pane>
      ) : null}
      <Pane>
        {isLoading ? (
          <Spinner size={25} />
        ) : (
          filteredData?.map((d) => (
            <WidgetDetailCard details={d} key={d.id}></WidgetDetailCard>
          ))
        )}
        {data?.length === 0 ? (
          <Pane>
            <Text fontSize={'1rem'} display="block">
              No ratings yet.
            </Text>
          </Pane>
        ) : null}
      </Pane>
    </Card>
  )
}
export default RatingsWidget
